import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { URLQuery } from "@/utils/queryUtils";
import { numberOrZero } from "@/utils/utils";
import { Card, Divider, Popconfirm, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  useDeleteVehicleCategory,
  useFilterVehicleCategories,
} from "../../../../../common/api/queries/vehicleCategory";
import ActionTextIcon from "../../../../../common/components/icons/ActionTextIcon";
import PopconfirmDeleteIcon from "../../../../../common/components/icons/PopconfirmDeleteIcon";
import Ellipsis from "../../../../../common/components/views/Ellipsis";
import { TableSizes } from "../../../../../common/constants";
import { CertificateVehicleCategory, CertificateVehicleCategoryFilterPageRequest } from "../../types";

interface Props {
  searchParams: CertificateVehicleCategoryFilterPageRequest;
  onUpdateClick: (certificateVehicleCategory: CertificateVehicleCategory) => void;
}

export const VehicleCategoryTable = ({ searchParams, onUpdateClick }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, isFetching } = useFilterVehicleCategories(searchParams);
  const deleteVehicleCategory = useDeleteVehicleCategory();

  const columns: ColumnsType<CertificateVehicleCategory> = [
    {
      key: "category",
      title: t("contract.enums.vehicleCategory._label"),
      width: 150,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{t("contract.enums.vehicleCategory." + record.category)}</Ellipsis>,
    },
    {
      key: "value",
      title: t("vehicleCategory.attrs.value"),
      width: 100,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.value}</Ellipsis>,
    },
    {
      key: "description",
      title: t("vehicleCategory.attrs.description"),
      width: 470,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.description}</Ellipsis>,
    },
    {
      key: "actions",
      align: "right",
      fixed: "right",
      width: 180,
      render: (_, record) => (
        <>
          <ActionTextIcon icon="edit" color="blue" text={t("common.edit")} onClick={() => onUpdateClick(record)} />

          <Divider orientation="vertical" />

          <Popconfirm
            title={t("vehicleCategory.helpers.deleteConfirm")}
            icon={<PopconfirmDeleteIcon />}
            cancelText={t("common.no")}
            okText={t("common.yes")}
            okType="danger"
            onConfirm={() => deleteVehicleCategory.mutate(record.id)}
          >
            <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <Card className="card-box">
      <Table<CertificateVehicleCategory>
        {...tableStandardProps()}
        columns={columns}
        loading={isFetching}
        dataSource={data?.pageData}
        scroll={{ x: TableSizes.MEDIUM }}
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(data?.pageIndex) + 1,
          pageSize: data?.pageSize,
          total: data?.totalElementsCount,
          onChange: (pageIndex) => navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
        }}
      />
    </Card>
  );
};
